<script setup>
	import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
	import { DsIconButton, DsText } from '@coloplast/design-system';
	import { useStore } from 'vuex';
	import { track_animation_interaction } from 'shared/services/tracking/contentTrackingService';

	const store = useStore();

	const activeHighlight = computed(() => store.getters['getActiveHighlight']);
	const setActiveHighlight = (payload) => store.commit('setActiveHighlight', payload);

	const isFirstInteraction = computed(() => store.getters['getIsFirstInteraction']);
	const setIsFirstInteraction = (payload) => store.commit('setIsFirstInteraction', payload);

	const props = defineProps({
		item: {
			type: Object,
			default: () => {},
		},
		ordinalNumber: {
			type: Number,
			required: true,
		},
	});

	const showHighlight = () => {
		if (props.ordinalNumber !== activeHighlight.value) {
			setActiveHighlight(props.ordinalNumber);
			if (!isFirstInteraction.value) {
				setIsFirstInteraction(true);
			}
			track_animation_interaction('view_details');
		} else if (props.ordinalNumber === activeHighlight.value) {
			setActiveHighlight(null);
		}
	};

	const windowWidth = ref(window.innerWidth);
	const tooltipWidthWithMargin = 326;

	const targetElement = ref(null);
	const targetRect = ref(null);

	const initialTooltipPosition = ref(props.item.HighlightAppears);
	const tooltipPosition = ref(props.item.HighlightAppears);

	onMounted(() => {
		window.addEventListener('resize', handleResize);
		handleResize();
	});

	onBeforeUnmount(() => {
		window.removeEventListener('resize', handleResize);
	});

	const handleResize = () => {
		windowWidth.value = window.innerWidth;
		getBoundingRectValues();
		changeTooltipPosition();
	};

	const getBoundingRectValues = async () => {
		await nextTick();

		if (targetElement.value) {
			targetRect.value = targetElement.value?.getBoundingClientRect();
		}
	};

	const changeTooltipPosition = () => {
		if (
			initialTooltipPosition.value === '-right' &&
			tooltipWidthWithMargin >= windowWidth.value - targetRect.value?.right
		) {
			tooltipPosition.value = '-left';
		} else if (
			initialTooltipPosition.value === '-left' &&
			tooltipWidthWithMargin >= targetRect.value?.left
		) {
			tooltipPosition.value = '-right';
		} else {
			tooltipPosition.value = initialTooltipPosition.value;
		}
	};
</script>

<template>
	<div
		v-if="item.Headline || item.Description"
		ref="targetElement"
		class="c-product-hotspot"
		:class="{ 'c-product-hotspot--active': ordinalNumber === activeHighlight }"
	>
		<div>
			<DsIconButton
				:aria-label="'Open info box: ' + item.Headline"
				icon-name="add"
				variant="secondary"
				class="c-product-hotspot__button"
				:class="{
					'c-product-hotspot__button--add': ordinalNumber !== activeHighlight,
					'c-product-hotspot__button c-product-hotspot__button--close': ordinalNumber === activeHighlight,
				}"
				:click-handler="showHighlight"
			/>
			<div
				class="c-product-hotspot__button-area"
			>
			</div>
		</div>
		<Transition
			name="highlight"
			appear
		>
			<div
				v-show="ordinalNumber === activeHighlight"
				class="c-product-hotspot__dialogue-box"
				:class="{ 'c-product-hotspot__dialogue-box--right': tooltipPosition === '-right' }"
			>
				<DsText
					v-if="item.Headline"
					html-element="h3"
					text-type="body-sm"
					text-style="bold"
					class="c-product-hotspot__headline"
				>
					{{ item.Headline }}
				</DsText>
				<DsText
					v-if="item.Description"
					text-type="label-sm"
					class="c-product-hotspot__description"
				>
					{{ item.Description }}
				</DsText>
			</div>
		</Transition>
	</div>
</template>
